import { api } from "./api_axios";

// ---- auth helpers ----
export const getCsrf = async () => (await api.get('/auth/csrf/')).data;
export const getMe = async () => (await api.get('/auth/me/')).data;

// dj-rest-auth login accepts username or email + password.
// To be universal with your settings (email OR username), send both keys.
export const postLogin = async ({ identifier, password }) => {
  const isEmail = /\S+@\S+\.\S+/.test(identifier);
  const payload = isEmail
    ? { email: identifier, password }
    : { username: identifier, password };
  const { data } = await api.post('/auth/login/', payload);
  return data; // session cookie is set by the response
};

export const postLogout = async () => (await api.post('/auth/logout/', {})).data;

export const postRegister = async ({ username, email, password1, password2 }) => {
  const { data } = await api.post('/auth/registration/', {
    username, email, password1, password2,
  });
  return data;
};

// ---- Game Helpers ----
export async function getGames(params = {}) {
  const { data } = await api.get('/games/games/', { params });
  return data;
}

export async function getAllGames(params = {}) {
  const games = [];
  let url = '/games/games/';
  let requestParams = { ordering: 'title', ...params };
  while (url) {
    const { data } = await api.get(url, { params: requestParams });
    games.push(...(data.results || data || []));
    url = data.next || null;
    requestParams = undefined;
  }
  return games;
}

export async function getSubscriptionPlans(params = {}) {
  const { data } = await api.get('/subscriptions/plans/', { params });
  return data;
}

export async function getSubscriptions(params = {}) {
  const { data } = await api.get('/subscriptions/subscriptions/', { params });
  return data;
}

export async function getGameOwnerships(params = {}) {
  const { data } = await api.get('/games/ownerships/', { params });
  return data;
}

export async function getPlayHistory() {
  const sessions = [];
  let url = '/games/sessions/history/';
  while (url) {
    const { data } = await api.get(url);
    sessions.push(...(data.results || data || []));
    url = data.next || null;
  }
  return sessions;
}

export async function getTournamentHistory() {
  const runs = [];
  let url = '/games/tournament-runs/history/';
  while (url) {
    const { data } = await api.get(url);
    runs.push(...(data.results || data || []));
    url = data.next || null;
  }
  return runs;
}

// ---- Survey helpers ----
export async function getPocketPlaySurvey() {
  const { data } = await api.get('/surveys/pocketplay/');
  return data;
}

export async function savePocketPlaySurvey({ answers, action, expectedRevision }) {
  const { data } = await api.put('/surveys/pocketplay/', {
    answers,
    action,
    expected_revision: expectedRevision,
  });
  return data;
}

export async function postSubscriptionCheckout({
  planId,
  billingInterval,
  successUrl,
  cancelUrl,
}) {
  const { data } = await api.post('/stripe-pay/checkout/subscription/', {
    plan_id: planId,
    billing_interval: billingInterval,
    success_url: successUrl,
    cancel_url: cancelUrl,
  });
  return data;
}

export async function postGameCheckout({
  gameId,
  successUrl,
  cancelUrl,
}) {
  const { data } = await api.post('/stripe-pay/checkout/game/', {
    game_id: gameId,
    success_url: successUrl,
    cancel_url: cancelUrl,
  });
  return data;
}

export async function postCancelSubscription() {
  const { data } = await api.post('/stripe-pay/subscription/cancel/', {});
  return data;
}

export async function postSubscriptionChange({
  planId,
  billingInterval,
}) {
  const { data } = await api.post('/stripe-pay/subscription/change/', {
    plan_id: planId,
    billing_interval: billingInterval,
  });
  return data;
}

export async function getGame(id) {
  const { data } = await api.get(`/games/games/${id}/`);
  return data;
}

export async function getGameByTitle(title) {
  const { data } = await api.get(`/games/games/`, { params: { title } });
  console.log('[API] getGameByTitle:', title, '->', data);
  return data.results[0] || null;
}

export async function getGameMusic(gameId) {
  const { data } = await api.get(`/music/tracks/for-game/${gameId}/`);
  return data;
}

export async function postGameAccess(params) {
  const gameId = typeof params === "object" ? params.gameId : params;
  const clientSessionKey = typeof params === "object" ? params.clientSessionKey : null;
  const { data } = await api.post(`/games/games/${gameId}/request-access/`, {
    ...(clientSessionKey ? { client_session_key: clientSessionKey } : {}),
  }, {
    timeout: 20000,
  });
  return data;
}

export async function postSessionEnd(sessionId) {
  const { data } = await api.post(`/games/sessions/end-session/`, { sessionId });
  return data;
}

export async function postSessionKeepAlive(sessionId, gameInfo = null) {
  const { data } = await api.post(`/games/sessions/keep-alive/`, {
    sessionId,
    gameInfo,
  });
  return data;
}

export async function postSessionGameFinished(sessionId, gameInfo = null) {
  const { data } = await api.post(`/games/sessions/game-finished/`, {
    sessionId,
    gameInfo,
  });
  return data;
}

// ---- Tournament helpers ----
export async function getTournaments(params = {}) {
  return (await api.get('/games/tournaments/', { params })).data;
}

export async function getAllTournaments() {
  const tournaments = [];
  let url = '/games/tournaments/';
  let params = { ordering: 'name' };
  while (url) {
    const { data } = await api.get(url, { params });
    tournaments.push(...(data.results || data || []));
    url = data.next || null;
    params = undefined;
  }
  return tournaments;
}

export async function getTournament(id) {
  return (await api.get(`/games/tournaments/${id}/`)).data;
}

export async function createTournament(payload) {
  return (await api.post('/games/tournaments/', payload)).data;
}

export async function updateTournament(id, payload) {
  return (await api.put(`/games/tournaments/${id}/`, payload)).data;
}

export async function startTournamentRun(tournamentId) {
  return (await api.post(`/games/tournaments/${tournamentId}/start-run/`, {})).data;
}

export async function getTournamentRun(runId) {
  return (await api.get(`/games/tournament-runs/${runId}/`)).data;
}

export async function startTournament(runId) {
  return (await api.post(`/games/tournament-runs/${runId}/start/`, {})).data;
}

export async function getTournamentHostTicket(runId) {
  return (await api.post(`/games/tournament-runs/${runId}/host-ticket/`, {})).data;
}

export async function prepareTournamentRound(runId, restart = false) {
  return (await api.post(`/games/tournament-runs/${runId}/prepare-round/`, { restart })).data;
}

export async function finishTournamentRound(runId, sessionId, gameInfo) {
  return (await api.post(`/games/tournament-runs/${runId}/round-finished/`, {
    sessionId,
    gameInfo,
  })).data;
}

export async function createRecoveryCode(runId, controllerId) {
  return (await api.post(`/games/tournament-runs/${runId}/new-recovery-code/`, { controllerId })).data;
}

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return "";
}

function buildApiUrl(path) {
  const base = String(api.defaults.baseURL || "").replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return base ? `${base}${suffix}` : suffix;
}

export function postSessionEndBestEffort(sessionId) {
  if (!sessionId || typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }

  const url = buildApiUrl("/games/sessions/end-session/");
  const csrfToken = getCookie("csrftoken");

  try {
    window
      .fetch(url, {
        method: "POST",
        credentials: "include",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
        },
        body: JSON.stringify({ sessionId }),
      })
      .catch(() => {});
  } catch {}
}
